import uniq from 'lodash/uniq';

import exifr from 'exifr';
import type { AssetContainerModel, AssetSaveRequest } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AssetMaxDimensions,
    AssetMinDimensions,
    AssetTypes,
    LicenseEnum,
    SlideBackgroundModelTypeEnum,
    SourceEnum,
} from 'js/src/client';
import { getUploadSourceFromContext } from 'js/src/components/app/editor-tabs/EditorTabsContext';
import { Toast } from 'js/src/components/common/Toast';
import { getCurrentEditorTab } from 'js/src/data/app';
import type {
    SearchContextValueType,
    StoredAssetContainer,
    StoredMediaResult,
    UploadSearchContextType,
} from 'js/src/data/media';
import {
    addMediaToUploads,
    addRecentlyUploadedAssetToAFolder,
    createFolderForFiles,
    getActiveSearchContextForCurrentTab,
    getCurrentMediaGenerations,
    getOrCreateRecordingFolder,
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
    isIconMimeType,
    RECORDING_FOLDER_NAME,
    SCREEN_RECORDING_FOLDER_NAME,
    SEARCH_CONTEXT,
    selectActiveMediaFolder,
    setCurrentMediaGenerations,
    slideBackgroundModelIsImageBackgroundModel,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';
import type {
    FileMetadata,
    FileUpload,
    FileWithMetadata,
    UploadAssetContainerTypes,
    UploadOptions,
} from 'js/src/data/uploads';
import {
    addFiles,
    assetContainerModelIsAudio,
    assetContainerModelIsBackground,
    assetContainerModelIsIcon,
    createUploadSession,
    deleteUploadSession,
    endUploadExperience,
    FILE_STATUS,
    fileUploadAssetIsAssetContainer,
    finalizeAttributionData,
    getAreFilesFinishedUploading,
    getFileObj,
    getFilesCanBeEdited,
    getUploadSession,
    readonlyAssetContainerModelIsBackground,
    readonlyAssetContainerModelIsIcon,
    isBackgroundAndIconAssetContainerType,
    markFileAsDeleted,
    markFileAsDeleting,
    markFileAsDeletionFailed,
    markFileAsFinalizing,
    removeUpload,
    selectAllFilesInUploadSession,
    selectUploadsAttribution,
    selectUploadsFolderId,
    selectUploadsLicense,
    selectUploadsMediaTags,
    setUploadFolder,
    storeCompletedFile,
    storeFailedUpload,
    storeSuccessfulUpload,
    storeUpdatedFileMetadata,
    storeUploadProgress,
    updateUploadedAssetMediaTags,
    updateUploadMediaTags,
    UploadingContext,
    userCancelFile,
} from 'js/src/data/uploads';
import { type DeepReadonly, isNotNull, toMutableCopy } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getFileMimeType } from 'js/src/libs/native-file-handler';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import { convertSvgStyleToAttrs } from 'js/src/libs/svgo';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import uuid from 'js/src/libs/uuid';
import { appendScreenRecordingSuffix } from 'js/src/luminary/luminary-utils';
import { isApiErrorWithProperty } from 'js/src/types';

export class FinalizeUploadsAbortError extends Error {}
export type UploadRecordingType =
    | typeof UploadingContext.VideoRecording
    | typeof UploadingContext.VoiceRecording
    | typeof UploadingContext.ScreenRecording;

// exifr unwraps single-item XMP lists, so a lone keyword field (often "a, b, c") arrives as a bare string.
const parseXmpKeywords = (subject: unknown): string[] => {
    const keywords = Array.isArray(subject) ? subject : [subject];
    return keywords
        .filter((keyword): keyword is string => typeof keyword === 'string')
        .flatMap((keyword) => keyword.split(','))
        .map((keyword) => keyword.trim())
        .filter(Boolean);
};

/**
 * Use this class when you want the uploaded files to be saved into the user's media library. Uploaded files will also
 * call saveValidatedAsset(), which manages licensing/attribution/tags for the created assets. Underneath the hood this class
 * uses UploadMediaController to communicate with our backend to handle the upload steps (all the way until the asset is created).
 *
 * Metadata: Note that in upload-media-controller, FileObj is { file: File, metadata: FileMetadata }, but in here we store more
 * information about the upload status as well with the file, so the file's metadata is in the higher level in FileUpload
 */
export class UploadsOperations {
    static #buildRecordingUploadOpts(file: File, uploadType: UploadRecordingType): UploadOptions {
        switch (uploadType) {
            case UploadingContext.VideoRecording:
                return {
                    file_list: [{ file }],
                    is_user_upload: false,
                    asset_type: AssetContainerTypes.BACKGROUND,
                    add_to_user_library: false,
                    max_height: AssetMaxDimensions.MAX_PIXELS,
                    max_width: AssetMaxDimensions.MAX_PIXELS,
                    ignoreOtherUploads: true,
                    analyticsContext: { trigger: 'Record Yourself' },
                };
            case UploadingContext.VoiceRecording:
                return {
                    file_list: [{ file }],
                    is_user_upload: false,
                    asset_type: AssetContainerTypes.AUDIO,
                    add_to_user_library: false,
                    ignoreOtherUploads: false,
                    metadata: { source: SourceEnum.SOURCE_USER_VOICEOVER },
                    analyticsContext: { trigger: 'Record Yourself' },
                };
            case UploadingContext.ScreenRecording:
                return {
                    file_list: [{ file }],
                    is_user_upload: false,
                    asset_type: AssetContainerTypes.BACKGROUND,
                    add_to_user_library: false,
                    max_height: AssetMaxDimensions.MAX_PIXELS,
                    max_width: AssetMaxDimensions.MAX_PIXELS,
                    ignoreOtherUploads: true,
                    metadata: { source_id: appendScreenRecordingSuffix(file.name) },
                    analyticsContext: { trigger: 'Record Yourself' },
                };
            default:
                throw new Error('Invalid upload type for recording upload');
        }
    }

    static async #resolveRecordingFolder(dispatch: AppDispatch, uploadType: UploadRecordingType) {
        if (uploadType === UploadingContext.VideoRecording) {
            return dispatch(getOrCreateRecordingFolder(RECORDING_FOLDER_NAME));
        }
        if (uploadType === UploadingContext.ScreenRecording) {
            return dispatch(getOrCreateRecordingFolder(SCREEN_RECORDING_FOLDER_NAME));
        }
        return null;
    }

    static #dispatchRecordingUpload(
        dispatch: AppDispatch,
        uploadOpts: UploadOptions,
        uploadType: UploadRecordingType,
        recordingFolder: Awaited<ReturnType<ReturnType<typeof getOrCreateRecordingFolder>>> | null,
        preuploadedGcs?: { bucket: string; file_name: string; asyncOperationUuid: string },
    ) {
        const uploadSessionId = uuid();
        dispatch(createUploadSession({ uploadSessionId }));
        const uploadPromise = dispatch(
            UploadsOperations.startUpload(uploadSessionId, uploadOpts, preuploadedGcs),
        );

        if (
            uploadType === UploadingContext.VideoRecording ||
            uploadType === UploadingContext.ScreenRecording
        ) {
            if (!recordingFolder) {
                throw new Error('Could not find recording folder');
            }
            dispatch(
                setUploadFolder({
                    uploadSessionId,
                    folderId: recordingFolder.id,
                }),
            );
        }
        uploadPromise.then(() => {
            dispatch(setUploadFolder({ uploadSessionId: undefined, folderId: null }));
        });
        return uploadPromise;
    }

    /**
     * The recording bytes are already in GCS (streamed during recording via
     * `RecordingUploader`), so we skip the GCS PUT and feed the existing
     * finalization pipeline (validation Celery chain, Pushpin polling, Redux +
     * analytics) directly.
     *
     * If `transcriptWords` is supplied (collected by `ScribeRealtimeClient`
     * during recording), it is attached as `script_with_word_timings` on the
     * upload metadata. The backend's `_generate_transcript` Celery stage
     * detects that field and short-circuits its batch transcription call,
     * `SpeechToText.format_elevenlabs_transcript` instead, and runs language
     * detection on the transcript text itself.
     */
    static uploadRecordingFromGCS(
        gcsRef: { bucket: string; file_name: string; asyncOperationUuid: string },
        uploadType: UploadRecordingType,
        recordingMeta: { fileName: string; mimeType: string },
        transcriptWords?: FileMetadata['script_with_word_timings'],
    ) {
        return async (dispatch: AppDispatch, _getState: GetRootState) => {
            const recordingFolder = await UploadsOperations.#resolveRecordingFolder(
                dispatch,
                uploadType,
            );

            // Synthetic File for the callback/Redux/analytics machinery — the bytes are
            // already in GCS so the size is irrelevant here, only the name + mime type.
            const file = new File([], recordingMeta.fileName, { type: recordingMeta.mimeType });

            const uploadOpts = UploadsOperations.#buildRecordingUploadOpts(file, uploadType);
            // We did the recording on the system itself, so we
            // already know the browser can decode it — set `can_background_transcode`
            // directly. The normal File-based probe in UploadMediaController would
            // resolve negatively here because our synthetic File has zero bytes
            // (the real bytes are in GCS), which would fall through to the
            // blocking transcoder path on the backend.
            uploadOpts.file_list = uploadOpts.file_list.map((entry) => ({
                ...entry,
                metadata: { ...(entry.metadata ?? {}), can_background_transcode: true },
            }));
            if (transcriptWords && transcriptWords.length > 0) {
                uploadOpts.metadata = {
                    ...(uploadOpts.metadata ?? {}),
                    script_with_word_timings: transcriptWords,
                };
            }
            return UploadsOperations.#dispatchRecordingUpload(
                dispatch,
                uploadOpts,
                uploadType,
                recordingFolder,
                gcsRef,
            );
        };
    }

    static canUploadDifferentAssetType(assetType: AssetContainerTypes) {
        /**
         * A user can upload two media types simultaneously only when
         * a) asset is either  `background_asset` or `audio_asset` ( different from SingleFileUpload )
         *
         * In all other scenarios either
         * 1) user doesn't have enough privileges to upload audio asset, thus
         * selected_file_asset_type === upload_media_controller asset type.
         *          OR
         * 2) user has privileges but still not selecting a valid asset type.
         * */
        return (
            assetType === AssetContainerTypes.BACKGROUND ||
            assetType === AssetContainerTypes.AUDIO ||
            assetType === AssetContainerTypes.ICON
        );
    }

    static startUpload(
        uploadSessionId: string,
        opts: UploadOptions,
        preuploadedGcs?: { bucket: string; file_name: string; asyncOperationUuid: string },
    ) {
        return async (dispatch: AppDispatch) => {
            opts.file_list = await UploadsOperations.#preprocessFiles(opts);
            dispatch(addFiles({ uploadSessionId, opts }));
            return dispatch(
                UploadsOperations.#setupAndRunUploadMediaController(
                    uploadSessionId,
                    opts,
                    preuploadedGcs,
                ),
            );
        };
    }

    static async #preprocessFiles(opts: UploadOptions): Promise<FileWithMetadata[]> {
        if (opts.asset_type !== AssetContainerTypes.ICON) {
            return opts.file_list;
        }

        const results = await Promise.allSettled(
            opts.file_list.map(async (fileWithMetadata) => {
                return new Promise<FileWithMetadata>((resolve) => {
                    const file_obj = fileWithMetadata.file;
                    const fr = new FileReader();
                    fr.onload = async (e: ProgressEvent<FileReader>) => {
                        const target = e.target;
                        if (!target) {
                            resolve(fileWithMetadata);
                            return;
                        }

                        const fileContent = target.result as string;
                        try {
                            const processedFile = await convertSvgStyleToAttrs(
                                fileContent,
                                file_obj.name,
                            );
                            const result = {
                                file: processedFile || file_obj,
                                metadata: fileWithMetadata.metadata,
                            };
                            resolve(result);
                        } catch (error) {
                            resolve(fileWithMetadata);
                        }
                    };
                    fr.onerror = () => {
                        resolve(fileWithMetadata);
                    };
                    fr.readAsText(fileWithMetadata.file);
                });
            }),
        );

        const maybeProcessedFiles = results.map((result) => {
            if (result.status === 'rejected') {
                throw Error('Unexpected promise rejected');
            }
            return result.value;
        });

        return maybeProcessedFiles;
    }

    static #setupAndRunUploadMediaController(
        uploadSessionId: string,
        opts: UploadOptions,
        preuploadedGcs?: { bucket: string; file_name: string; asyncOperationUuid: string },
    ) {
        return (dispatch: AppDispatch, getState: GetRootState) => {
            return new Promise<Array<string>>((resolve, reject) => {
                opts.progress_callback = (progress, session_id, file_progress) => {
                    dispatch(storeUploadProgress({ progress, session_id, file_progress }));
                };
                opts.upload_succeeded_callback = async (
                    file_with_metadata,
                    new_asset,
                    new_asset_type,
                    asyncOperationUuid,
                ) => {
                    const file_obj = file_with_metadata.file;
                    if (
                        new_asset_type === opts.asset_type ||
                        UploadsOperations.canUploadDifferentAssetType(new_asset_type)
                    ) {
                        let tags: string[] = [];
                        // If folder path is provided, then add the folder name as tags to the file.
                        if (file_obj?.webkitRelativePath && SERVER.USER.can_upload_media_folders) {
                            const path = file_obj.webkitRelativePath;
                            const folderNamesAsTags = path.split('/');
                            if (folderNamesAsTags.length) {
                                folderNamesAsTags.pop(); // remove the filename
                                tags = [...folderNamesAsTags];
                            }
                        }

                        // Get the Metadata only for Image Assets( primarily XMP data )
                        const uploadedAssetOfType = new_asset[new_asset_type];
                        if (
                            uploadedAssetOfType &&
                            'type' in uploadedAssetOfType &&
                            uploadedAssetOfType.type === 'image'
                        ) {
                            try {
                                const metadata = await exifr.parse(file_obj, {
                                    xmp: true,
                                    mergeOutput: false,
                                });
                                //  refer https://wwwimages2.adobe.com/content/dam/acom/en/devnet/xmp/pdfs/XMP%20SDK%20Release%20cc-2016-08/XMPSpecificationPart3.pdf  for XMP specification.
                                if (metadata?.dc?.subject) {
                                    tags = [...tags, ...parseXmpKeywords(metadata.dc.subject)];
                                }
                            } catch (error) {
                                // do nothing
                            }
                        }

                        if (tags.length) {
                            dispatch(
                                updateUploadedAssetMediaTags({
                                    uploadSessionId,
                                    file_obj,
                                    tags,
                                }),
                            );
                        }
                        dispatch(
                            storeSuccessfulUpload({
                                uploadSessionId,
                                file_obj,
                                new_asset,
                                new_asset_type,
                                async_operation_uuid: asyncOperationUuid,
                            }),
                        );
                    } else {
                        dispatch(
                            storeFailedUpload({
                                uploadSessionId,
                                file_obj,
                                message: 'This file type is not supported',
                            }),
                        );
                    }

                    // canFilesBeEdited is whether the modal is still open (which means tags/attribution
                    // could still be modified, so don't finalize yet)
                    const canFilesBeEdited = getFilesCanBeEdited(getState(), uploadSessionId);
                    if (canFilesBeEdited) {
                        // files can still be edited, so we don't finalize yet
                        const promise = dispatch(
                            UploadsOperations.finalizeUploads(
                                uploadSessionId,
                                opts.ignoreOtherUploads,
                                opts.metadata,
                            ),
                        );
                        promise
                            .then((assetIds) => {
                                resolve(assetIds);
                            })
                            .catch((err) => reject(err));
                    } else {
                        // no editing possible, finalize the individual file immediately
                        const promise = dispatch(
                            UploadsOperations.finalizeIndividualFile(
                                uploadSessionId,
                                asyncOperationUuid,
                                opts.metadata,
                            ),
                        );
                        promise
                            .then((assetId) => {
                                // Check if all files are done and clean up session if so
                                dispatch(
                                    UploadsOperations.checkAndCleanupSession(
                                        uploadSessionId,
                                        opts.ignoreOtherUploads,
                                    ),
                                );
                                resolve(assetId ? [assetId] : []);
                            })
                            .catch((err) => reject(err));
                    }
                };
                opts.upload_failed_callback = (file_with_metadata, message, error) => {
                    const file_obj = file_with_metadata.file;
                    dispatch(
                        storeFailedUpload({
                            uploadSessionId,
                            file_obj,
                            message,
                        }),
                    );
                    dispatch(
                        UploadsOperations.finalizeUploads(uploadSessionId, opts.ignoreOtherUploads),
                    ).catch(() => {
                        // catch so the error isn't sent to sentry
                    });
                    reject(error);
                };
                opts.metadata_updated_callback = (file_with_metadata, new_metadata) => {
                    if (!getUploadSession(getState(), uploadSessionId)) {
                        // the upload session ended before this callback was called!
                        return;
                    }
                    dispatch(
                        storeUpdatedFileMetadata({
                            uploadSessionId,
                            file_with_metadata,
                            new_metadata,
                        }),
                    );
                };

                // Pass the uploadSessionId to the controller for progress tracking
                opts.session_id = uploadSessionId;

                const upload_media_controller = new UploadMediaController(opts);
                if (preuploadedGcs) {
                    // Recording was streamed to GCS during recording; skip
                    // _upload_file's GCS PUT and run only the finalization steps.
                    upload_media_controller.uploadPreuploaded(opts.file_list[0], preuploadedGcs);
                } else {
                    upload_media_controller.upload();
                }
            });
        };
    }

    static addMetadataToPendingUpload(
        uploadSessionId: string,
        file: FileUpload,
        new_metadata: FileMetadata,
    ) {
        return (dispatch: AppDispatch) => {
            const old_file_obj = getFileObj(file);
            const old_file_metadata = toMutableCopy<FileMetadata>(file.file_metadata);
            const { metadata: old_uploads_metadata, ...old_upload_opts } = file.upload_opts;

            const new_file_obj = new File([old_file_obj], old_file_obj.name, {
                type: getFileMimeType(old_file_obj),
            });
            // Remove duration from metadata to prevent it from being stored
            // and sent to the backend.
            const undefinedDurationMetadata: FileMetadata = {
                duration: undefined,
            };
            const new_file_metadata = {
                ...old_file_metadata,
                ...new_metadata,
                ...undefinedDurationMetadata,
            };
            const new_uploads_metadata = {
                ...(old_uploads_metadata && toMutableCopy<FileMetadata>(old_uploads_metadata)),
                ...new_metadata,
                ...undefinedDurationMetadata,
            };

            const new_upload_opts = {
                ...old_upload_opts,
                ...{
                    file_list: [
                        {
                            file: new_file_obj,
                            metadata: new_file_metadata,
                        },
                    ],
                    metadata: new_uploads_metadata,
                },
            };

            dispatch(userCancelFile({ uploadSessionId, file_obj: old_file_obj }));
            return dispatch(UploadsOperations.startUpload(uploadSessionId, new_upload_opts));
        };
    }

    /**
    This function returns a thunk, which returns a Promise. The Promise is resolved if uploads is complete
    and rejected if files can still be modified or uploads are still in progress
    @param ignoreOtherUploads - boolean, if true, we don't wait for other uploads to complete to finalize this upload
    @param metadata - FileMetadata object, additional metadata to be used when finalizing the upload
    @return a Promise
    */
    static finalizeUploads(
        uploadSessionId: string,
        ignoreOtherUploads = false,
        metadata: FileMetadata = {},
    ) {
        return async (dispatch: AppDispatch, getState: GetRootState) => {
            const filesCanBeEdited = getFilesCanBeEdited(getState(), uploadSessionId);
            if (filesCanBeEdited) {
                // we are not ready to finalize yet, the list of files to save/delete
                // could still be altered by the user.
                return Promise.reject(
                    new Error('Not ready to finalize, files can still be altered by user'),
                );
            }

            // check that all file uploads are finished before proceeding
            const areFilesFinishedUploading = getAreFilesFinishedUploading(
                getState(),
                uploadSessionId,
            );
            if (!areFilesFinishedUploading) {
                // we are not ready to finalize yet, the list of files to save/delete are still uploading
                return Promise.reject(
                    new FinalizeUploadsAbortError(
                        'Not ready to finalize, waiting until all files are ready',
                    ),
                );
            }

            return new Promise<Array<string>>((resolve, reject) => {
                const files = selectAllFilesInUploadSession(getState(), uploadSessionId);
                const complete_promises: Promise<string | null>[] = [];
                files.forEach((file) => {
                    if (
                        file.user_canceled === true &&
                        [FILE_STATUS.VALIDATED, FILE_STATUS.FAILED].indexOf(file.status) >= 0
                    ) {
                        // the file hasn't been deleted from GCS yet, and it was cancelled by the user.
                        // do the deletion now.
                        complete_promises.push(
                            dispatch(UploadsOperations.removeFileFromGCS(uploadSessionId, file)),
                        );
                    } else if (
                        file.user_canceled === false &&
                        [FILE_STATUS.VALIDATED].indexOf(file.status) >= 0
                    ) {
                        // merge the automatic parsed tags
                        const fileTags = [...file.tags];
                        const mediaTags = uniq([
                            ...selectUploadsMediaTags(getState(), uploadSessionId),
                            ...fileTags,
                        ]);
                        const folderId =
                            selectUploadsFolderId(getState(), uploadSessionId) ||
                            file.file_metadata.folderId ||
                            null;
                        const attribution = selectUploadsAttribution(getState(), uploadSessionId);
                        const license = selectUploadsLicense(getState(), uploadSessionId);
                        const asyncOperationUuid = file.asyncOperationUuid;
                        // the file hasn't been saved to the DB yet, do that now.
                        const asset_type = file.upload_opts.asset_type;
                        complete_promises.push(
                            dispatch(
                                UploadsOperations.saveValidatedAsset({
                                    uploadSessionId,
                                    file,
                                    attribution,
                                    license,
                                    asset_type,
                                    mediaTags,
                                    fileTags,
                                    userProvidedTags: fileTags,
                                    folderId,
                                    asyncOperationUuid,
                                    metadata,
                                }),
                            ),
                        );
                    }
                });

                Promise.all(complete_promises)
                    .then((assetIds) => {
                        // Remove null values from deleted files and undefined values
                        // saveValidatedAsset resolves undefined when the given asset_type is different from the response type
                        const nonEmptyAssetIds = assetIds.filter(isNotNull);

                        if (ignoreOtherUploads) {
                            resolve(nonEmptyAssetIds);
                            return;
                        }

                        const files = selectAllFilesInUploadSession(getState(), uploadSessionId);
                        let all_complete = true;
                        files.forEach((file) => {
                            if (
                                [
                                    FILE_STATUS.LOADING,
                                    FILE_STATUS.FINALIZING,
                                    FILE_STATUS.DELETING,
                                    FILE_STATUS.VALIDATED,
                                ].indexOf(file.status) >= 0
                            ) {
                                all_complete = false;
                            }
                        });

                        if (all_complete) {
                            dispatch(
                                updateUploadMediaTags({
                                    uploadSessionId,
                                    mediaTags: [],
                                }),
                            );
                            dispatch(
                                finalizeAttributionData({
                                    uploadSessionId,
                                    attribution: '',
                                    license: LicenseEnum.USER_PRIVATE,
                                }),
                            );
                            resolve(nonEmptyAssetIds);
                        } else {
                            reject(new Error('Upload is not complete.'));
                        }
                    })
                    .finally(() => {
                        // Clean up upload session
                        dispatch(deleteUploadSession({ uploadSessionId }));
                    });
            });
        };
    }

    // Process individual file immediately when it finishes uploading
    static finalizeIndividualFile(
        uploadSessionId: string,
        asyncOperationUuid: string,
        metadata: FileMetadata = {},
    ) {
        return async (dispatch: AppDispatch, getState: GetRootState) => {
            const files = selectAllFilesInUploadSession(getState(), uploadSessionId);

            // Find the specific file that just finished uploading
            const targetFile = files.find(
                (file) =>
                    file.asyncOperationUuid === asyncOperationUuid &&
                    file.status === FILE_STATUS.VALIDATED &&
                    !file.user_canceled,
            );

            if (!targetFile) {
                return null; // File not found or not ready
            }

            // Prepare file metadata
            const asset_type = targetFile.upload_opts.asset_type;
            const fileTags = [...targetFile.tags];
            const mediaTags = uniq([
                ...selectUploadsMediaTags(getState(), uploadSessionId),
                ...fileTags,
            ]);
            const folderId =
                selectUploadsFolderId(getState(), uploadSessionId) ||
                targetFile.file_metadata.folderId ||
                null;
            const attribution = selectUploadsAttribution(getState(), uploadSessionId);
            const license = selectUploadsLicense(getState(), uploadSessionId);

            // Save the individual file immediately
            return dispatch(
                UploadsOperations.saveValidatedAsset({
                    uploadSessionId,
                    file: targetFile,
                    attribution,
                    license,
                    asset_type,
                    mediaTags,
                    fileTags,
                    userProvidedTags: fileTags,
                    folderId,
                    asyncOperationUuid,
                    metadata,
                }),
            );
        };
    }

    // Check if all files are done and clean up session if needed
    static checkAndCleanupSession(uploadSessionId: string, ignoreOtherUploads = false) {
        return (dispatch: AppDispatch, getState: GetRootState) => {
            if (ignoreOtherUploads) {
                return; // Don't clean up if we're ignoring other uploads
            }

            const files = selectAllFilesInUploadSession(getState(), uploadSessionId);
            let allComplete = true;

            files.forEach((file) => {
                const status = file.status;
                const userCanceled = file.user_canceled;

                // Check if any files are still processing
                if (
                    !userCanceled &&
                    (status === FILE_STATUS.LOADING ||
                        status === FILE_STATUS.FINALIZING ||
                        status === FILE_STATUS.VALIDATED)
                ) {
                    allComplete = false;
                }
            });

            // Clean up session if all files are complete
            if (allComplete) {
                dispatch(deleteUploadSession({ uploadSessionId }));
            }
        };
    }

    static removeFileFromGCS(uploadSessionId: string, file: FileUpload) {
        const get_urls_from_asset = (uploaded_asset: DeepReadonly<AssetContainerModel>) => {
            let urls: Array<string | null> = [];
            if (uploaded_asset.type === AssetContainerTypes.BACKGROUND) {
                const slideBackground = uploaded_asset.background;
                if (slideBackground?.type === SlideBackgroundModelTypeEnum.IMAGE) {
                    const image = slideBackground.image;
                    urls.push(image?.render_url ?? null);
                    urls.push(image?.preview_url ?? null);
                    urls.push(image?.thumbnail_url ?? null);
                } else if (slideBackground?.type === SlideBackgroundModelTypeEnum.VIDEO) {
                    const video = slideBackground.video;
                    urls.push(video?.render_url ?? null);
                    urls.push(video?.preview_url ?? null);
                    urls.push(video?.thumbnail_url ?? null);
                    urls.push(video?.poster_url ?? null);
                } else {
                    throw new Error('The type needs to be set inside of the SlideBackgroundModel');
                }
            } else if (uploaded_asset.type === AssetContainerTypes.AUDIO) {
                urls.push(uploaded_asset.audio?.mp3_url ?? null);
                urls.push(uploaded_asset.audio?.orig_mp3_url ?? null);
            } else if (uploaded_asset.type === AssetContainerTypes.ICON) {
                urls.push(uploaded_asset.icon?.url ?? null);
            } else if (uploaded_asset.type === AssetContainerTypes.DOCUMENT) {
                urls.push(uploaded_asset.document?.url ?? null);
            } else if (uploaded_asset.type === AssetContainerTypes.FONT) {
                urls.push(uploaded_asset.font?.url ?? null);
            } else {
                throw new Error('Unknown asset type while removing file from gcs');
            }
            urls = urls.filter((url) => url); // remove any null urls (sometimes render_url isn't available, etc)
            const unique_urls = new Set(urls); // remove duplicates
            return Array.from(unique_urls);
        };

        return (dispatch: AppDispatch) => {
            return new Promise<null>((resolve, reject) => {
                const uploadedAsset = file.uploaded_asset;
                if (!uploadedAsset) {
                    // if the asset doesn't exist, we can just mark the file
                    // as deleted, we don't have to request it's deletion
                    dispatch(
                        markFileAsDeleted({
                            uploadSessionId,
                            file_id: file.id,
                        }),
                    );
                    resolve(null);
                    return;
                }

                if (!fileUploadAssetIsAssetContainer(uploadedAsset)) {
                    throw new Error('Unknown asset type while removing file from gcs');
                }
                // get the GCS urls from the asset
                const urls = get_urls_from_asset(uploadedAsset);
                dispatch(
                    markFileAsDeleting({
                        uploadSessionId,
                        file_id: file.id,
                    }),
                );

                ApiService.assetDeletionGcsDestroy({
                    query: { urls: JSON.stringify(urls) },
                })
                    .then(() => {
                        dispatch(
                            markFileAsDeleted({
                                uploadSessionId,
                                file_id: file.id,
                            }),
                        );
                        resolve(null);
                    })
                    .catch(() => {
                        dispatch(
                            markFileAsDeletionFailed({
                                uploadSessionId,
                                file_id: file.id,
                            }),
                        );
                        resolve(null);
                    });
            });
        };
    }

    static saveValidatedAsset({
        uploadSessionId,
        file,
        attribution,
        license,
        asset_type,
        mediaTags = [],
        fileTags = [],
        userProvidedTags = [],
        folderId = null,
        asyncOperationUuid,
        metadata = {},
    }: {
        uploadSessionId: string;
        file: FileUpload;
        attribution: string;
        license: LicenseEnum;
        asset_type: AssetContainerTypes;
        mediaTags?: string[];
        fileTags?: string[];
        userProvidedTags?: string[];
        folderId: number | null;
        asyncOperationUuid?: string;
        metadata?: FileMetadata;
    }) {
        const createSaveAssetRequestData = (
            uploadedAsset: DeepReadonly<AssetContainerModel>,
            attribution: string,
            license: LicenseEnum,
            mediaTags: string[],
            folderId: number | null,
            metadata: FileMetadata,
            asyncOperationUuid?: string,
        ): AssetSaveRequest => {
            const assetContainer = toMutableCopy<AssetContainerModel>(uploadedAsset);
            switch (assetContainer.type) {
                case AssetContainerTypes.BACKGROUND: {
                    const background_asset_type = assetContainer.background?.type;
                    if (!background_asset_type) {
                        throw new Error(
                            'The type needs to be set inside of the SlideBackgroundModel',
                        );
                    }
                    const backgroundAsset = assetContainer.background?.[background_asset_type];
                    if (!backgroundAsset) {
                        throw new Error('The background asset must hold an actual asset');
                    }
                    backgroundAsset.license = license;
                    backgroundAsset.attribution = attribution;
                    if (mediaTags) {
                        backgroundAsset.tags = mediaTags;
                    }

                    if (folderId) {
                        assetContainer.folder_id = folderId;
                    }

                    if (metadata.article_id) {
                        assetContainer.article_id = metadata.article_id;
                    } else if (metadata.document_id) {
                        assetContainer.document_id = metadata.document_id;
                    }

                    break;
                }
                case AssetContainerTypes.ICON: {
                    const iconAsset = assetContainer.icon;
                    if (!iconAsset) {
                        throw new Error('The icon asset container must hold an icon');
                    }
                    iconAsset.license = license;
                    iconAsset.attribution = attribution;
                    if (mediaTags) {
                        iconAsset.tags = mediaTags;
                    }

                    if (folderId) {
                        assetContainer.folder_id = folderId;
                    }
                    break;
                }
                case AssetContainerTypes.AUDIO: {
                    const audioAsset = assetContainer.audio;
                    if (!audioAsset) {
                        throw new Error('The audio asset container must hold an audio asset');
                    }
                    audioAsset.license = license;
                    audioAsset.attribution = attribution;
                    break;
                }
                default:
                    break;
            }
            const { visual_description: _readOnly, ...assetToSave } = assetContainer;
            return {
                uploaded_asset: assetToSave,
                async_operation_uuid: asyncOperationUuid,
            };
        };

        return (dispatch: AppDispatch) => {
            return new Promise<string | null>((resolve, reject) => {
                const uploadedAsset = file.uploaded_asset;
                if (!uploadedAsset || !fileUploadAssetIsAssetContainer(uploadedAsset)) {
                    throw new Error('The uploaded asset must be set before saving the asset');
                }
                const saveAssetRequestData = createSaveAssetRequestData(
                    uploadedAsset,
                    attribution,
                    license,
                    mediaTags,
                    folderId,
                    metadata,
                    asyncOperationUuid,
                );
                dispatch(
                    markFileAsFinalizing({
                        uploadSessionId,
                        file_id: file.id,
                    }),
                );

                ApiService.assetSaveCreate({
                    body: saveAssetRequestData,
                })
                    .then((response) => {
                        let assetId = null;
                        const uploadedAsset = response.data.uploaded_asset;
                        if (
                            uploadedAsset.type === asset_type ||
                            UploadsOperations.canUploadDifferentAssetType(uploadedAsset.type)
                        ) {
                            const newAsset = uploadedAsset[uploadedAsset.type];
                            if (!newAsset) {
                                throw new Error('asset container must hold an actual asset');
                            }
                            dispatch(
                                storeCompletedFile({
                                    uploadSessionId,
                                    file_id: file.id,
                                    new_asset: newAsset, // nigel todo: this seems like it needs to be different for audio?
                                    new_asset_type: uploadedAsset.type,
                                }),
                            );

                            // save the id's for the current session uploads.
                            if (assetContainerModelIsBackground(uploadedAsset)) {
                                const underlyingAsset =
                                    uploadedAsset.background[uploadedAsset.background.type];
                                if (!underlyingAsset) {
                                    throw new Error(
                                        'The background asset must hold an actual Image/Video/Icon asset',
                                    );
                                }
                                assetId = underlyingAsset.uuid;

                                analytics.track('Media Upload Confirmed', {
                                    media_file_contains_tags: fileTags.length ? 'Yes' : 'No',
                                    media_file_tags: fileTags,
                                    media_user_provided_tags: userProvidedTags,
                                });
                            }
                            if (assetContainerModelIsIcon(uploadedAsset)) {
                                assetId = uploadedAsset.icon.uuid;

                                analytics.track('Icon Upload Confirmed', {
                                    media_file_contains_tags: fileTags.length ? 'Yes' : 'No',
                                    media_file_tags: fileTags,
                                    media_user_provided_tags: userProvidedTags,
                                });
                            }
                            if (
                                uploadedAsset.folder_id &&
                                assetContainerModelIsBackground(uploadedAsset)
                            ) {
                                dispatch(
                                    addRecentlyUploadedAssetToAFolder({
                                        folderId: uploadedAsset.folder_id,
                                        mediaAsset: uploadedAsset,
                                    }),
                                );
                            }
                            if (
                                uploadedAsset.folder_id &&
                                assetContainerModelIsIcon(uploadedAsset)
                            ) {
                                dispatch(
                                    addRecentlyUploadedAssetToAFolder({
                                        folderId: uploadedAsset.folder_id,
                                        mediaAsset: uploadedAsset,
                                    }),
                                );
                            }
                            // add recently uploaded media to our media redux store
                            if (
                                !uploadedAsset.folder_id &&
                                assetContainerModelIsBackground(uploadedAsset)
                            ) {
                                const backgroundAsset = uploadedAsset.background;
                                let isSticker = false;
                                if (slideBackgroundModelIsImageBackgroundModel(backgroundAsset)) {
                                    isSticker =
                                        backgroundAsset.image.source ===
                                        SourceEnum.SOURCE_USER_STICKERS;
                                } else if (
                                    slideBackgroundModelIsVideoBackgroundModel(backgroundAsset)
                                ) {
                                    isSticker =
                                        backgroundAsset.video.source ===
                                        SourceEnum.SOURCE_USER_STICKERS;
                                }

                                if (isSticker) {
                                    dispatch(
                                        addMediaToUploads({
                                            mediaAsset: uploadedAsset,
                                            search_context: SEARCH_CONTEXT.UPLOADED_ELEMENTS,
                                        }),
                                    );
                                } else {
                                    dispatch(
                                        addMediaToUploads({
                                            mediaAsset: uploadedAsset,
                                            search_context: SEARCH_CONTEXT.UPLOADED_VISUALS,
                                        }),
                                    );
                                }
                            }
                            if (
                                !uploadedAsset.folder_id &&
                                assetContainerModelIsIcon(uploadedAsset)
                            ) {
                                dispatch(
                                    addMediaToUploads({
                                        mediaAsset: uploadedAsset,
                                        search_context: SEARCH_CONTEXT.UPLOADED_ELEMENTS,
                                    }),
                                );
                            }
                            if (assetContainerModelIsAudio(uploadedAsset)) {
                                assetId = uploadedAsset.audio.uuid;
                                if (
                                    [
                                        SourceEnum.SOURCE_AI_VOICEOVER,
                                        SourceEnum.SOURCE_USER_VOICEOVER,
                                    ].includes(uploadedAsset.audio.source)
                                ) {
                                    if (uploadedAsset.audio.has_voiceover) {
                                        dispatch(
                                            addMediaToUploads({
                                                mediaAsset: uploadedAsset,
                                                search_context: SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
                                            }),
                                        );
                                    } else {
                                        dispatch(
                                            storeFailedUpload({
                                                uploadSessionId,
                                                file_obj: getFileObj(file),
                                                message:
                                                    "We weren't able to detect any speech in your audio file. Try another file or upload music tracks to the Music tab.",
                                            }),
                                        );
                                    }
                                } else if (uploadedAsset.audio.source === SourceEnum.SOURCE_USER) {
                                    dispatch(
                                        addMediaToUploads({
                                            mediaAsset: uploadedAsset,
                                            search_context: SEARCH_CONTEXT.UPLOADED_AUDIO,
                                        }),
                                    );
                                }
                            }
                        } else {
                            dispatch(
                                storeFailedUpload({
                                    uploadSessionId,
                                    file_obj: getFileObj(file),
                                    message: 'Invalid file type for the uploader.',
                                }),
                            );
                        }
                        resolve(assetId);
                    })
                    .catch((err) => {
                        ErrorReporter.capture(err);
                        let message =
                            'An unknown error occurred saving your upload, please try again.';
                        if (isApiErrorWithProperty(err, 'message')) {
                            message = err.message;
                        }
                        dispatch(
                            storeFailedUpload({
                                uploadSessionId,
                                file_obj: getFileObj(file),
                                message,
                            }),
                        );
                        resolve(null);
                    });
            });
        };
    }

    static endUpload(uploadSessionId: string, folderId: number | null = null) {
        return (dispatch: AppDispatch) => {
            dispatch(endUploadExperience({ uploadSessionId, folderId }));
            return dispatch(UploadsOperations.finalizeUploads(uploadSessionId));
        };
    }

    static archiveUpload(
        asset: StoredAssetContainer | StoredMediaResult,
        searchContext: SearchContextValueType | null,
    ) {
        return (dispatch: AppDispatch, getState: GetRootState) => {
            const state = getState();
            const activeFolderId = selectActiveMediaFolder(state, searchContext);

            let assetId = null;
            let assetUuid: string | null = null;
            let assetType: AssetTypes | null = null;
            let assetSource: SourceEnum | null = null;
            if (readonlyAssetContainerModelIsBackground(asset)) {
                const background = asset[AssetContainerTypes.BACKGROUND];
                if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
                    const image = background[SlideBackgroundModelTypeEnum.IMAGE];
                    assetId = image.id;
                    assetUuid = image.uuid;
                    assetType = AssetTypes.IMAGE;
                    assetSource = image.source;
                } else if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
                    const video = background[SlideBackgroundModelTypeEnum.VIDEO];
                    assetId = video.id;
                    assetUuid = video.uuid;
                    assetType = AssetTypes.VIDEO;
                    assetSource = video.source;
                }
            } else if (readonlyAssetContainerModelIsIcon(asset)) {
                assetId = asset[AssetContainerTypes.ICON].id;
                assetType = AssetTypes.ICON;
            }

            const isAIGeneratedMedia = assetSource === SourceEnum.SOURCE_AI_GENERATED;

            if (
                searchContext !== SEARCH_CONTEXT.UPLOADED_VISUALS &&
                searchContext !== SEARCH_CONTEXT.UPLOADED_ELEMENTS &&
                searchContext !== SEARCH_CONTEXT.AI_GENERATED_MEDIA &&
                searchContext !== SEARCH_CONTEXT.FAVORITE_AI_MEDIA &&
                !(searchContext === null && isAIGeneratedMedia)
            ) {
                // users are only allowed to delete from their uploaded visuals, icons, stickers, and AI generated media
                return;
            }

            // Handle unsaved AI media (no assetId) - just remove from local generations list
            if (!assetId && isAIGeneratedMedia && assetUuid) {
                // Remove from current generations
                const currentGenerations = getCurrentMediaGenerations(state);
                const filteredCurrentGenerations = currentGenerations.filter((gen) => {
                    const genAsset = gen?.result?.asset;
                    const genUuid =
                        genAsset?.background?.image?.uuid || genAsset?.background?.video?.uuid;
                    return genUuid !== assetUuid;
                });
                if (filteredCurrentGenerations.length !== currentGenerations.length) {
                    dispatch(setCurrentMediaGenerations(filteredCurrentGenerations));
                }
                return;
            }

            if (!assetId || !assetType) {
                throw new Error('Cannot find the asset id to archive');
            }

            ApiService.backgroundAssetsDestroy({
                query: {
                    id: assetId,
                    type: assetType,
                },
            })
                .then(() => {
                    // nothing to do here, the asset is removed from the backend
                })
                .catch(() => {
                    Toast.error(
                        'Something went wrong and the media item could not be removed, please try again.',
                    );
                });

            // optimistically dispatch the removal
            const effectiveSearchContext =
                searchContext || (isAIGeneratedMedia && SEARCH_CONTEXT.AI_GENERATED_MEDIA);
            if (effectiveSearchContext) {
                dispatch(
                    removeUpload({
                        asset,
                        search_context: effectiveSearchContext,
                        activeFolderId,
                    }),
                );
            }
        };
    }
}

export const getUploadOptions = (
    assetContainerTypes: UploadAssetContainerTypes,
    files: File[],
    onlyImages: boolean,
    analyticsTrigger: string,
) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const is_element = isBackgroundAndIconAssetContainerType(assetContainerTypes);

        const filesMap: Partial<Record<AssetContainerTypes, File[]>> = {};
        if (is_element) {
            // Separate files into asset container types and create options for each type
            const backgroundFiles: File[] = [];
            const iconFiles: File[] = [];

            files.forEach((file) => {
                const mimeType = getFileMimeType(file);
                if (isIconMimeType(mimeType)) {
                    iconFiles.push(file);
                } else {
                    // place all other files in background, let file validation handle errors
                    backgroundFiles.push(file);
                }
            });

            filesMap[AssetContainerTypes.BACKGROUND] = backgroundFiles;
            filesMap[AssetContainerTypes.ICON] = iconFiles;
        } else {
            filesMap[assetContainerTypes] = files;
        }

        const editorTab = getCurrentEditorTab(getState());
        const searchContext = getActiveSearchContextForCurrentTab(getState());

        const uploadOptions: UploadOptions[] = [];
        for (const assetContainerType of Object.keys(filesMap) as Array<AssetContainerTypes>) {
            let fileList = filesMap[assetContainerType];
            if (!fileList || !fileList.length) {
                continue;
            }

            let options: UploadOptions = {
                asset_type: assetContainerType,
                add_to_user_library: false,
                min_width: AssetMinDimensions.MIN_USER_UPLOAD_PIXELS,
                min_height: AssetMinDimensions.MIN_USER_UPLOAD_PIXELS,
                max_width: AssetMaxDimensions.MAX_PIXELS,
                max_height: AssetMaxDimensions.MAX_PIXELS,
                only_images: assetContainerType === AssetContainerTypes.BACKGROUND && onlyImages,
                metadata: {
                    is_element,
                    source: getUploadSourceFromContext(
                        editorTab,
                        assetContainerType === AssetContainerTypes.ICON,
                    ),
                },
                file_list: [] as FileWithMetadata[],
                analyticsContext: {
                    trigger: analyticsTrigger,
                },
            };

            // if files have folder information , extract that information.
            if (fileList[0].webkitRelativePath && SERVER.USER.can_upload_media_folders) {
                // Remove unrecognized files.
                fileList = fileList.filter((file) => file.type !== ''); // usually file with no type are system files or unrecognized files.
                if (fileList.length > 0) {
                    options = await dispatch(
                        createFolderForFiles(
                            fileList,
                            options,
                            searchContext as UploadSearchContextType,
                        ),
                    );
                }
            } else {
                options['file_list'] = fileList.map((file) => ({ file }));
            }

            uploadOptions.push(options);
        }

        return uploadOptions;
    };
};
